/**
 * Static Secret Dialog
 *
 * Modal for pasting a secret value for a static-credential login. Follows
 * the dialog-overlay/dialog pattern from
 * features/devcontainer/components/admin/FeatureFormDialog.tsx.
 */

import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { LoginStatus } from '../types';

interface StaticSecretDialogProps {
  open: boolean;
  login: LoginStatus | null;
  onClose: () => void;
  onSubmit: (secret: string) => void;
  isSubmitting: boolean;
}

export function StaticSecretDialog({
  open,
  login,
  onClose,
  onSubmit,
  isSubmitting,
}: StaticSecretDialogProps): ReactElement | null {
  const [secret, setSecret] = useState('');

  useEffect(() => {
    setSecret('');
  }, [login, open]);

  if (!open || !login) return null;

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!secret) return;
    onSubmit(secret);
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={`Set secret for ${login.display_name}`}
      >
        <h2>Set secret — {login.display_name}</h2>
        <form onSubmit={handleSubmit} className="dialog__form">
          <label className="dialog__field">
            {login.secret_label ?? `${login.display_name} secret`}
            <input
              type="password"
              value={secret}
              onChange={(e) => { setSecret(e.target.value); }}
              autoComplete="off"
              required
            />
          </label>
          <p className="dialog__help">
            {login.help_text ??
              'This value is stored securely and used to authenticate on your behalf.'}
          </p>
          <div className="dialog__actions">
            <button
              type="button"
              onClick={onClose}
              className="dialog__btn dialog__btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dialog__btn dialog__btn--primary"
              disabled={isSubmitting || !secret}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
